import {
    ensureAssetsLoaded,
    initMoyasarForm,
    prepareMoyasarPayment,
} from "@/services/paymentHandlers/moyasarShared";

const POPUP_STYLE_ID = "moyasar-popup-style";

let activePopupCleanup;

function ensurePopupStyles() {
    if (document.getElementById(POPUP_STYLE_ID)) return;

    const style = document.createElement("style");
    style.id = POPUP_STYLE_ID;
    style.textContent = `
        .moyasar-popup-mask {
            position: fixed;
            inset: 0;
            z-index: 9999;
            background: rgba(0, 0, 0, 0.45);
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 24px;
            box-sizing: border-box;
        }

        .moyasar-popup-card {
            width: min(420px, 100%);
            max-height: min(760px, calc(100vh - 48px));
            background: #fff;
            border-radius: 24px;
            padding: 20px 20px 24px;
            box-sizing: border-box;
            box-shadow: 0 14px 36px rgba(0, 0, 0, 0.18);
            overflow-y: auto;
            -webkit-overflow-scrolling: touch;
        }

        .moyasar-popup-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 12px;
            margin-bottom: 16px;
        }

        .moyasar-popup-title {
            font-size: 22px;
            line-height: 1.2;
            font-weight: 600;
            color: #222;
        }

        .moyasar-popup-close {
            border: 0;
            background: transparent;
            color: #444;
            font-size: 30px;
            line-height: 1;
            cursor: pointer;
            padding: 0;
        }

        .moyasar-popup-body {
            min-height: 72px;
        }

        .moyasar-popup-body .mysr-form {
            margin: 0;
        }
    `;
    document.head.appendChild(style);
}

function closeActivePopup() {
    if (typeof activePopupCleanup === "function") {
        activePopupCleanup();
        activePopupCleanup = undefined;
    }
}

function createPopup({ title }) {
    closeActivePopup();
    ensurePopupStyles();

    const mask = document.createElement("div");
    mask.className = "moyasar-popup-mask";

    const card = document.createElement("div");
    card.className = "moyasar-popup-card";

    const header = document.createElement("div");
    header.className = "moyasar-popup-header";

    const heading = document.createElement("div");
    heading.className = "moyasar-popup-title";
    heading.textContent = title;

    const closeButton = document.createElement("button");
    closeButton.type = "button";
    closeButton.className = "moyasar-popup-close";
    closeButton.setAttribute("aria-label", "Close payment popup");
    closeButton.textContent = "×";

    const body = document.createElement("div");
    body.className = "moyasar-popup-body";

    header.appendChild(heading);
    header.appendChild(closeButton);
    card.appendChild(header);
    card.appendChild(body);
    mask.appendChild(card);
    document.body.appendChild(mask);

    const cleanup = () => {
        closeButton.removeEventListener("click", cleanup);
        if (mask.parentNode) {
            mask.parentNode.removeChild(mask);
        }
        if (activePopupCleanup === cleanup) {
            activePopupCleanup = undefined;
        }
    };

    closeButton.addEventListener("click", cleanup);
    activePopupCleanup = cleanup;

    return {
        element: body,
        close: cleanup,
    };
}

export default {
    async initPayment() {
        return null;
    },
    async checkAvailability({ selectedMethod }) {
        if (selectedMethod === "creditcard") {
            return { available: true };
        }

        if (selectedMethod === "applepay") {
            try {
                const available = Boolean(
                    window.ApplePaySession &&
                        typeof window.ApplePaySession.canMakePayments ===
                            "function" &&
                        window.ApplePaySession.canMakePayments()
                );
                return {
                    available,
                    reason: available
                        ? "Apple Pay available"
                        : "Apple Pay is not available on this device",
                };
            } catch (error) {
                return {
                    available: false,
                    reason: error?.message || "Apple Pay is not available",
                };
            }
        }

        return { available: false };
    },
    async handlePayment({
        selectedMethod,
        selectedSubscription,
        deviceCode,
        outletId,
        config,
        authorizationSubscriptionId,
        createPaymentOrder,
        doSubscriptionPayment,
        toast,
        hideLoading,
    }) {
        let popup;

        try {
            const {
                amount,
                currency,
                givenId,
                internalId,
                publishableKey,
            } = await prepareMoyasarPayment({
                selectedMethod,
                selectedSubscription,
                deviceCode,
                outletId,
                config,
                authorizationSubscriptionId,
                createPaymentOrder,
                doSubscriptionPayment,
            });

            await ensureAssetsLoaded();

            popup = createPopup({
                title:
                    selectedMethod === "applepay"
                        ? "Apple Pay"
                        : "Card Payment",
            });

            initMoyasarForm({
                element: popup.element,
                selectedMethod,
                amount,
                currency,
                internalId,
                givenId,
                publishableKey,
                config,
            });
            hideLoading();
        } catch (error) {
            if (popup) {
                popup.close();
            } else {
                closeActivePopup();
            }
            hideLoading();
            toast(error?.message || "Payment failed");
        }
    },
};
